import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { CourseUser } from 'graphqlAPI'
import {
  createRouteCourseCertificatesGenerate,
  createRouteParticipantCourseResults,
} from 'enum/routes'
import { MonitoringStatus } from 'enum'
import { DropdownLoader } from 'components/ui/DropdownLoader'
import CustomLink from 'components/ui/CustomLink'
import { DropdownButton, styles } from 'components/ui/DropdownButton'
import { IMainState } from 'store/states'
import { useAccess } from 'contexts/AccessContext'
import { useDc3AttestationAssigned } from 'hooks/useDc3AttestationAssigned'
import {
  Dc3CertificateGenerateModal,
  Dc3GenerationMode,
} from 'components/dc3Certificate'
import optionStyles from './Options.module.scss'

type Data = CourseUser

interface IProps {
  courseUser: Data
  courseNormative?: boolean
  courseInactive?: boolean
  onFinish?: () => void
}

const Options: React.FC<IProps> = ({
  courseUser,
  courseNormative = false,
  courseInactive = false,
  onFinish,
}) => {
  const { t } = useTranslation()
  const loading = false
  const [show, setShow] = useState(false)
  const [generateModalOpen, setGenerateModalOpen] = useState(false)
  const [generateMode, setGenerateMode] =
    useState<Dc3GenerationMode>('download')

  const { access, can } = useAccess()

  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const { assigned: dc3AttestationAssigned } = useDc3AttestationAssigned(
    Number(courseUser.courseId),
    attestationsEnabled && courseNormative
  )

  const {
    userId,
    courseId,
    advance = MonitoringStatus.Pending,
    certificatesCount,
    normativeProfileIncomplete = false,
    user: { name, lastname },
  } = courseUser

  const handleDropdownClick = (showDropdown: boolean) => {
    setShow(showDropdown)
  }

  const disabled = advance === MonitoringStatus.Pending || courseInactive
  const canFinish = !!onFinish && can('manageProgress') && !courseInactive
  const canViewProfile = access.canViewParticipantProfile()
  const canViewResults = access.canViewParticipantResults()
  const canDownloadCertificate = access.canDownloadReportWithViewAccess()
  const showDownloadLegalAttestation =
    canDownloadCertificate && attestationsEnabled && courseNormative === true
  const downloadLegalAttestationDisabled =
    courseInactive ||
    !dc3AttestationAssigned ||
    normativeProfileIncomplete === true ||
    advance !== MonitoringStatus.Finish

  const handleDownloadLegalAttestation = () => {
    setShow(false)
    setGenerateMode('download')
    setGenerateModalOpen(true)
  }

  const participantName = [name, lastname].filter(Boolean).join(' ').trim()

  const hasVisibleOptions =
    canFinish ||
    canViewProfile ||
    canViewResults ||
    (canDownloadCertificate && !!certificatesCount) ||
    showDownloadLegalAttestation

  if (!hasVisibleOptions) {
    return null
  }

  return (
    <>
      <div
        className={show ? optionStyles.menuOpen : undefined}
        data-menu-open={show ? 'true' : undefined}
      >
        <DropdownButton
          show={show}
          className={styles.actionSelect}
          alignRight
          title={<DropdownLoader loading={loading} />}
          onClick={() => handleDropdownClick(!show)}
          onToggle={() => handleDropdownClick(!show)}
        >
          {canFinish && (
            <a
              className={styles['option-item']}
              onClick={() => {
                setShow(false)
                onFinish?.()
              }}
            >
              {t('administration.monitor.buttons.finish')}
            </a>
          )}
          {canViewProfile && (
            <CustomLink
              className={styles['option-item']}
              to={`/configuration/user-profile/${userId}`}
              disabled={courseInactive}
            >
              {t('monitoring.participants.actions.view')}
            </CustomLink>
          )}
          {canViewResults && (
            <CustomLink
              className={styles['option-item']}
              to={createRouteParticipantCourseResults({
                userId: `${userId}`,
                courseId,
              })}
              disabled={disabled}
            >
              {t('configuration.userProfile.tabs.courses.actions.viewResults', {
                defaultValue: 'Ver resultados',
              })}
            </CustomLink>
          )}
          {canDownloadCertificate && (
            <CustomLink
              className={styles['option-item']}
              to={createRouteCourseCertificatesGenerate(
                {
                  courseId: courseId,
                },
                {
                  users: [userId],
                  search: '',
                }
              )}
              disabled={courseInactive || !certificatesCount}
              target="_blank"
            >
              {t(
                'monitoring.course.statistics.table.actions.downloadCertificate',
                {
                  count: certificatesCount || 0,
                }
              )}
            </CustomLink>
          )}
          {showDownloadLegalAttestation && (
            <a
              className={
                downloadLegalAttestationDisabled
                  ? `${styles['option-item']} ${styles.disabled}`
                  : styles['option-item']
              }
              onClick={() => {
                if (!downloadLegalAttestationDisabled) {
                  handleDownloadLegalAttestation()
                }
              }}
            >
              {t(
                'monitoring.course.statistics.table.actions.downloadLegalAttestation'
              )}
            </a>
          )}
        </DropdownButton>
      </div>
      <Dc3CertificateGenerateModal
        open={generateModalOpen}
        mode={generateMode}
        courseId={Number(courseId)}
        userIds={[Number(userId)]}
        zipFileName={t('dc3Certificate.generateModal.participantZipFileName', {
          participantName,
        })}
        onClose={() => setGenerateModalOpen(false)}
      />
    </>
  )
}

export default Options
